import { Injectable } from '@angular/core';

import { ListResponse } from '../../models/list-response';
import {
  CustomerMembership,
  CustomerMembershipCancellationLog,
  CustomerMembershipCancellationLogFilters,
  CustomerMembershipFilters,
  CustomerMembershipFreezeLog,
  CustomerMembershipFreezeLogFilters, CustomerMembershipReferral, CustomerMembershipReferralFilters,
  Member,
  MemberFilters,
  Membership, MembershipCategory,
  MembershipDiscount,
  MembershipDiscountFilters,
  MembershipEntryTimeSlot,
  MembershipFilters, MembershipGiftDiscount, MembershipGiftDiscountFilters,
  MembershipGiftingConfig,
  MembershipLogReasonCount,
  MembershipPerCustomerAddOn,
  RosterMember,
  RosterMemberFilters,
} from '../../models/membership';

import { ApiService } from '../api/api.service';
import { AccountingConnection } from '../../models/organization-config';
import { getHttpParamsForFilters } from '../../utils';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MembershipService {
  /**
   * A service providing access to memberships via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of memberships from the API.
   */
  getMemberships(pageNumber?: number,
                 pageSize?: number,
                 filters?: MembershipFilters): Promise<ListResponse<Membership>> {
    return this.apiService.getCollection<Membership, MembershipFilters>(
      'memberships/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get an individual membership from the API.
   */
  getMembership(id: number): Promise<Membership> {
    return this.apiService.getItem<Membership>(
      'memberships/',
      id
    );
  }

  /**
   * Post a new membership to the API.
   */
  addMembership(membership: Membership): Promise<Membership> {
    return this.apiService.postToAPI<Membership>(
      'memberships/',
      membership
    );
  }

  /**
   * Update a membership via the API.
   */
  updateMembership(membership: Membership): Promise<Membership> {
    return this.apiService.patchToAPI<Membership>(
      membership.url,
      membership
    );
  }

  /**
   * Update a membership's account code configuration via the API.
   */
  updateMembershipRevenueAccount(conn: AccountingConnection,
                                 membership: Membership,
                                 accountCode: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-membership-revenue-account-code/' + conn.id.toString() + '/' + membership.id.toString() + '/',
      {account_code: accountCode}
    ).then();
  }

  /**
   * Update a particular membership add-on's account code configuration via the API.
   */
  updateMembershipAddOnRevenueAccount(conn: AccountingConnection,
                                      membership: Membership,
                                      addOn: MembershipPerCustomerAddOn,
                                      accountCode: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-membership-add-on-revenue-account-code/' + conn.id.toString() + '/' + membership.id.toString() + '/' + addOn.id + '/',
      {account_code: accountCode}
    ).then();
  }

  /**
   * Get a page of membership entry time slots for a given membership from the
   * API.
   *
   * Only relevant for memberships with has_limited_entry=true.
   */
  getMembershipEntryTimeSlots(membershipId: number,
                              pageNumber?: number,
                              pageSize?: number): Promise<ListResponse<MembershipEntryTimeSlot>> {
    return this.apiService.getCollectionNoFilters<MembershipEntryTimeSlot>(
      'memberships/' + membershipId.toString() + '/entry-time-slots/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new membership entry time slot to the API.
   *
   * Only relevant for memberships with has_limited_entry=true.
   */
  addMembershipEntryTimeSlot(membershipId: number,
                             membershipEntryTimeSlot: MembershipEntryTimeSlot): Promise<MembershipEntryTimeSlot> {
    // Treat empty string as undefined, so the error message shows that it was
    // not provided.
    if (membershipEntryTimeSlot.start_time === '') {
      membershipEntryTimeSlot.start_time = undefined;
    }
    if (membershipEntryTimeSlot.end_time === '') {
      membershipEntryTimeSlot.end_time = undefined;
    }

    return this.apiService.postToAPI<MembershipEntryTimeSlot>(
      'memberships/' + membershipId.toString() + '/entry-time-slots/',
      membershipEntryTimeSlot
    );
  }

  /**
   * Update a membership entry time slot by making a PUT request to the API.
   *
   * Only relevant for memberships with has_limited_entry=true.
   */
  updateMembershipEntryTimeSlot(membershipEntryTimeSlot: MembershipEntryTimeSlot): Promise<MembershipEntryTimeSlot> {
    // Treat empty string as undefined, so the error message shows that it was
    // not provided.
    if (membershipEntryTimeSlot.start_time === '') {
      membershipEntryTimeSlot.start_time = undefined;
    }
    if (membershipEntryTimeSlot.end_time === '') {
      membershipEntryTimeSlot.end_time = undefined;
    }

    return this.apiService.putToAPI<MembershipEntryTimeSlot>(
      membershipEntryTimeSlot.url,
      membershipEntryTimeSlot
    );
  }

  /**
   * Delete the given membership entry time slot.
   *
   * Only relevant for memberships with has_limited_entry=true.
   */
  deleteMembershipEntryTimeSlot(membershipEntryTimeSlotUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      membershipEntryTimeSlotUrl
    ).then();
  }

  getMembershipDiscounts(pageNumber?: number,
                         pageSize?: number,
                         filters?: MembershipDiscountFilters): Promise<ListResponse<MembershipDiscount>> {
    return this.apiService.getCollection<MembershipDiscount, MembershipDiscountFilters>(
      'membership-discounts/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getMembershipDiscount(id: number): Promise<MembershipDiscount> {
    return this.apiService.getItem<MembershipDiscount>(
      'membership-discounts/',
      id
    );
  }

  addMembershipDiscount(discount: MembershipDiscount): Promise<MembershipDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.postToAPI<MembershipDiscount>(
      'membership-discounts/',
      {
        discount_apply_method: discount.discount_apply_method,
        code: discount.code,
        name: discount.name,
        discount_type: discount.discount_type,
        amount: discount.amount,
        is_recurring: discount.is_recurring,
        applies_to_add_ons: discount.applies_to_add_ons,
        applies_to_initiation_fees: discount.applies_to_initiation_fees,
        waives_initiation_fees: discount.waives_initiation_fees,
        start_date: discount.start_date,
        end_date: discount.end_date,
        membership_ids: discount.membership_ids,
        roster_ids: discount.roster_ids,
        requires_association: discount.requires_association,
        requires_all_associations: discount.requires_all_associations,
        association_ids: discount.association_ids,
        requires_membership: discount.requires_membership,
        required_membership_ids: discount.required_membership_ids,
        max_number_of_uses_per_customer: discount.max_number_of_uses_per_customer,
        required_no_check_in_period_days: discount.required_no_check_in_period_days,
        applies_to_first_time_members_only: discount.applies_to_first_time_members_only,
        min_check_in_count: discount.min_check_in_count,
        max_check_in_count: discount.max_check_in_count,
        min_number_of_days_without_membership: discount.min_number_of_days_without_membership,
        min_age: discount.min_age,
        max_age: discount.max_age,
        required_active_duration_days: discount.required_active_duration_days,
        can_be_used_with_upfront_payments: discount.can_be_used_with_upfront_payments,
        referrer_credit_amount: discount.referrer_credit_amount,
        max_number_of_referrer_credits_per_month: discount.max_number_of_referrer_credits_per_month,
        referrer_credit_only_applies_to_recurring_payments: discount.referrer_credit_only_applies_to_recurring_payments,
        referrer_must_have_active_membership: discount.referrer_must_have_active_membership,
        may_combine_with_other_discounts: discount.may_combine_with_other_discounts,
        customer_message: discount.customer_message,
        minimum_duration_before_offer_days: discount.minimum_duration_before_offer_days,
        minimum_duration_before_offer_months: discount.minimum_duration_before_offer_months,
        purchase_alert_message: discount.purchase_alert_message,
        purchase_terms_and_conditions: discount.purchase_terms_and_conditions,
        change_payment_method_alert_message: discount.change_payment_method_alert_message,
        change_payment_method_terms_and_conditions: discount.change_payment_method_terms_and_conditions
      }
    );
  }

  updateMembershipDiscount(discount: MembershipDiscount): Promise<MembershipDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.patchToAPI<MembershipDiscount>(
      discount.url,
      {
        discount_apply_method: discount.discount_apply_method,
        code: discount.code,
        name: discount.name,
        discount_type: discount.discount_type,
        amount: discount.amount,
        is_recurring: discount.is_recurring,
        applies_to_add_ons: discount.applies_to_add_ons,
        applies_to_initiation_fees: discount.applies_to_initiation_fees,
        waives_initiation_fees: discount.waives_initiation_fees,
        start_date: discount.start_date,
        end_date: discount.end_date,
        membership_ids: discount.membership_ids,
        roster_ids: discount.roster_ids,
        requires_association: discount.requires_association,
        requires_all_associations: discount.requires_all_associations,
        association_ids: discount.association_ids,
        requires_membership: discount.requires_membership,
        required_membership_ids: discount.required_membership_ids,
        max_number_of_uses_per_customer: discount.max_number_of_uses_per_customer,
        required_no_check_in_period_days: discount.required_no_check_in_period_days,
        applies_to_first_time_members_only: discount.applies_to_first_time_members_only,
        min_check_in_count: discount.min_check_in_count,
        max_check_in_count: discount.max_check_in_count,
        min_number_of_days_without_membership: discount.min_number_of_days_without_membership,
        min_age: discount.min_age,
        max_age: discount.max_age,
        required_active_duration_days: discount.required_active_duration_days,
        can_be_used_with_upfront_payments: discount.can_be_used_with_upfront_payments,
        referrer_credit_amount: discount.referrer_credit_amount,
        max_number_of_referrer_credits_per_month: discount.max_number_of_referrer_credits_per_month,
        referrer_credit_only_applies_to_recurring_payments: discount.referrer_credit_only_applies_to_recurring_payments,
        referrer_must_have_active_membership: discount.referrer_must_have_active_membership,
        may_combine_with_other_discounts: discount.may_combine_with_other_discounts,
        customer_message: discount.customer_message,
        minimum_duration_before_offer_days: discount.minimum_duration_before_offer_days,
        minimum_duration_before_offer_months: discount.minimum_duration_before_offer_months,
        purchase_alert_message: discount.purchase_alert_message,
        purchase_terms_and_conditions: discount.purchase_terms_and_conditions,
        change_payment_method_alert_message: discount.change_payment_method_alert_message,
        change_payment_method_terms_and_conditions: discount.change_payment_method_terms_and_conditions
      }
    );
  }

  deleteMembershipDiscount(discount: MembershipDiscount): Promise<MembershipDiscount> {
    return this.apiService.deleteFromAPI<MembershipDiscount>(
      discount.url
    );
  }

  getMembershipGiftingConfigs(): Promise<MembershipGiftingConfig[]> {
    return this.apiService.getFromAPI<MembershipGiftingConfig[]>(
      'membership-gifting-configs/'
    );
  }

  getMembershipGiftingConfig(id: number): Promise<MembershipGiftingConfig> {
    return this.apiService.getItem<MembershipGiftingConfig>(
      'membership-gifting-configs/',
      id
    );
  }

  addMembershipGiftingConfig(giftingConfig: MembershipGiftingConfig): Promise<MembershipGiftingConfig> {
    return this.apiService.postToAPI<MembershipGiftingConfig>(
      'membership-gifting-configs/',
      giftingConfig
    );
  }

  updateMembershipGiftingConfig(giftingConfig: MembershipGiftingConfig): Promise<MembershipGiftingConfig> {
    return this.apiService.patchToAPI<MembershipGiftingConfig>(
      giftingConfig.url,
      giftingConfig
    );
  }

  deleteMembershipGiftingConfig(giftingConfig: MembershipGiftingConfig): Promise<MembershipGiftingConfig> {
    return this.apiService.deleteFromAPI<MembershipGiftingConfig>(
      giftingConfig.url
    );
  }

  assignGiftingConfigToMemberships(giftingConfigId: number,
                                   membershipIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'assign-gifting-config-to-memberships/',
      {
        gifting_config_id: giftingConfigId,
        membership_ids: membershipIds
      }
    ).then();
  }

  getMembershipGiftDiscounts(pageNumber?: number,
                             pageSize?: number,
                             filters?: MembershipGiftDiscountFilters): Promise<ListResponse<MembershipGiftDiscount>> {
    return this.apiService.getCollection<MembershipGiftDiscount, MembershipGiftDiscountFilters>(
      'membership-gift-discounts/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getMembershipGiftDiscount(id: number): Promise<MembershipGiftDiscount> {
    return this.apiService.getItem<MembershipGiftDiscount>(
      'membership-gift-discounts/',
      id
    );
  }

  addMembershipGiftDiscount(discount: MembershipGiftDiscount): Promise<MembershipGiftDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.postToAPI<MembershipGiftDiscount>(
      'membership-gift-discounts/',
      discount
    );
  }

  updateMembershipGiftDiscount(discount: MembershipGiftDiscount): Promise<MembershipGiftDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.patchToAPI<MembershipGiftDiscount>(
      discount.url,
      discount
    );
  }

  deleteMembershipGiftDiscount(discount: MembershipGiftDiscount): Promise<MembershipGiftDiscount> {
    return this.apiService.deleteFromAPI<MembershipGiftDiscount>(
      discount.url
    );
  }

  /**
   * Get a page of customer memberships from the API.
   */
  getCustomerMemberships(pageNumber?: number,
                         pageSize?: number,
                         filters?: CustomerMembershipFilters): Promise<ListResponse<CustomerMembership>> {
    return this.apiService.getCollection<CustomerMembership, CustomerMembershipFilters>(
      'customer-memberships/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  /**
   * Get an individual customer membership from the API.
   */
  getCustomerMembership(id: number): Promise<CustomerMembership> {
    return this.apiService.getItem<CustomerMembership>(
      'customer-memberships/',
      id
    );
  }

  getCustomerMembershipCancellationLogs(
    pageNumber?: number,
    pageSize?: number,
    filters?: CustomerMembershipCancellationLogFilters): Promise<ListResponse<CustomerMembershipCancellationLog>> {

    return this.apiService.getCollection<CustomerMembershipCancellationLog, CustomerMembershipCancellationLogFilters>(
      'metrics/membership-cancellation-logs/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  getCustomerMembershipFreezeLogs(
    pageNumber?: number,
    pageSize?: number,
    filters?: CustomerMembershipFreezeLogFilters): Promise<ListResponse<CustomerMembershipFreezeLog>> {

    return this.apiService.getCollection<CustomerMembershipFreezeLog, CustomerMembershipFreezeLogFilters>(
      'metrics/membership-freeze-logs/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  getCustomerMembershipCancellationLogReasonCounts(
    filters: CustomerMembershipCancellationLogFilters): Promise<MembershipLogReasonCount[]> {
    return this.apiService.getFromAPI<MembershipLogReasonCount[]>(
      'metrics/membership-cancellation-logs/reason-counts/',
      getHttpParamsForFilters(filters),
      true
    );
  }

  getCustomerMembershipFreezeLogReasonCounts(
    filters: CustomerMembershipFreezeLogFilters): Promise<MembershipLogReasonCount[]> {
    return this.apiService.getFromAPI<MembershipLogReasonCount[]>(
      'metrics/membership-freeze-logs/reason-counts/',
      getHttpParamsForFilters(filters),
      true
    );
  }

  getMembers(filters?: MemberFilters,
             includeBillingInfo?: boolean): Promise<Member[]> {
    let params = getHttpParamsForFilters(filters);
    if (includeBillingInfo) {
      params = params.append('include_billing_info', 'true');
    }
    return this.apiService.getFromAPI<Member[]>(
      'members/',
      params,
      includeBillingInfo
    );
  }

  getRosterMembers(filters?: RosterMemberFilters): Promise<RosterMember[]> {
    const params = getHttpParamsForFilters(filters);
    return this.apiService.getFromAPI<RosterMember[]>(
      'roster-members/',
      params,
      true
    );
  }

  getCustomerMembershipReferrals(pageNumber?: number,
                                 pageSize?: number,
                                 filters?: CustomerMembershipReferralFilters): Promise<ListResponse<CustomerMembershipReferral>> {
    return this.apiService.getCollection<CustomerMembershipReferral, CustomerMembershipReferralFilters>(
      'membership-referrals/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  getMembershipCategories(pageNumber?: number,
                          pageSize?: number): Promise<ListResponse<MembershipCategory>> {
    return this.apiService.getCollectionNoFilters<MembershipCategory>(
      'membership-categories/',
      pageNumber,
      pageSize
    );
  }

  getMembershipCategory(id: number): Promise<MembershipCategory> {
    return this.apiService.getItem<MembershipCategory>(
      'membership-categories/',
      id
    );
  }

  addMembershipCategory(category: MembershipCategory): Promise<MembershipCategory> {
    return this.apiService.postToAPI<MembershipCategory>(
      'membership-categories/',
      category
    );
  }

  updateMembershipCategory(category: MembershipCategory): Promise<MembershipCategory> {
    return this.apiService.patchToAPI<MembershipCategory>(
      category.url,
      category
    );
  }

  deleteMembershipCategory(category: MembershipCategory): Promise<void> {
    return this.apiService.deleteFromAPI<MembershipCategory>(
      category.url
    ).then();
  }

  setMembershipCategoriesOrderNumbers(categories: MembershipCategory[]): Promise<void> {
    const body = {};

    for (const category of categories) {
      body[category.id.toString()] = category.order_number;
    }

    return this.apiService.putToAPI<any>(
      environment.api_url + 'membership-categories-order-numbers/',
      body
    ).then();
  }
}
